import * as React from "react";
import type { ReviewTileItem } from "@/types/tile.types";
import type { ISubmitFeedbackRequest } from "@/types/discuss.types";
import type { WriteItRequest } from "@/types/review.types";
import { toast } from "sonner";
import { TbArrowsLeftRight } from "react-icons/tb";
import { FaRegCopy } from "react-icons/fa";
import { IoChatbubblesOutline } from "react-icons/io5";
import { FiEdit2 } from "react-icons/fi";
import { RiFileEditLine, RiCloseLargeFill } from "react-icons/ri";
import { ImStarEmpty } from "react-icons/im";
import { EditTileDialog } from "./EditTileDialog";

interface TileHooks {
  insertHook: {
    isInserting: boolean;
    insertingTileId: string | null;
    insertSingleTile: (tile: ReviewTileItem) => Promise<boolean>;
  };
  commentHook: {
    isAddingComments: boolean;
    addingCommentTileId: string | null;
    addSingleComment: (tile: ReviewTileItem) => Promise<boolean>;
  };
  copyHook: {
    isCopying: boolean;
    copyingTileId: string | null;
    copySingleTile: (tile: ReviewTileItem) => Promise<boolean>;
  };
  dismissHook: {
    dismissingTileId: string | null;
    dismissSingleTile: (tile: ReviewTileItem) => Promise<boolean>;
  };
  replaceHook: {
    isReplacing: boolean;
    replacingTileId: string | null;
    replaceSingleTile: (tile: ReviewTileItem) => Promise<boolean>;
  };
}

export function buildTileStatusPayload(documentId: string, item: ReviewTileItem) {
  return {
    document_id: [documentId],
    query_term: [item.query ?? ""],
    tile_ids: [item.originalData?.tile_id ?? 0],
    isCompleted: true,
    isFailed: false,
  };
}

export function shouldUpdateTileStatus(item: ReviewTileItem): boolean {
  return item.query != null && item.originalData?.tile_id !== undefined;
}

export async function handleRatingSubmit(
  rating: number,
  comment: string,
  documentId: string,
  item: ReviewTileItem,
  submitFeedback: (payload: ISubmitFeedbackRequest) => Promise<unknown>,
  setIsSubmittingRating: (v: boolean) => void,
  setShowRatingDialog: (v: boolean) => void
) {
  if (documentId.length === 0 || item.documentName == null) {
    toast.error("Submission Failed", {
      description: "Document context is missing.",
    });
    return;
  }

  setIsSubmittingRating(true);
  try {
    const feedbackPayload = {
      document_id: [documentId],
      source: "Review" as ISubmitFeedbackRequest["source"],
      prompt: item.queryType ?? null,
      query: item.query ?? "",
      response: item.analysis ?? item.explanation ?? item.reason ?? "",
      rating: rating as 1 | 2 | 3 | 4 | 5,
      comment: comment,
      current_document_id: documentId,
      document_title: [item.documentName],
    };

    await submitFeedback(feedbackPayload);

    toast.success("Feedback Submitted", {
      description: "Thank you for your feedback",
    });
    setShowRatingDialog(false);
  } catch (_error) {
    toast.error("Submission Failed", {
      description: "Failed to submit feedback. Please try again.",
    });
  } finally {
    setIsSubmittingRating(false);
  }
}

export function buildPrimaryActions(
  item: ReviewTileItem,
  hooks: TileHooks,
  handleAction: (action: string) => Promise<void>,
  isAnyOperationInProgress: boolean
) {
  const { insertHook, dismissHook, replaceHook } = hooks;

  return [
    {
      name: "close",
      icon: <RiCloseLargeFill className="size-3.5 text-neutral-400" />,
      tooltip: "Close tile",
      onClick: () => handleAction("close"),
      isLoading: dismissHook.dismissingTileId === item.id,
      disabled: isAnyOperationInProgress,
      className: "text-danger-200",
      condition: true,
    },
    {
      name: "edit",
      icon: (
        <EditTileDialog tile={item} queryTerm={item.query ?? ""}>
          <span>
            <RiFileEditLine className="size-3.5 text-neutral-400" />
          </span>
        </EditTileDialog>
      ),
      tooltip: "Edit tile",
      onClick: () => {},
      isLoading: false,
      disabled: isAnyOperationInProgress,
      condition: true,
    },
    {
      name: "write",
      icon: <FiEdit2 className="size-3.5 text-neutral-400" />,
      tooltip: "Write it",
      onClick: () => handleAction("write"),
      isLoading: insertHook.insertingTileId === item.id,
      disabled: isAnyOperationInProgress,
      condition: item.originalData?.status?.isWritable,
    },
    {
      name: "insert",
      icon: <TbArrowsLeftRight className="size-3.5 text-neutral-400" />,
      tooltip: "Replace Section",
      onClick: () => handleAction("insert"),
      isLoading: replaceHook.replacingTileId === item.id,
      disabled: isAnyOperationInProgress,
      condition: item.originalData?.status?.isReplaceable,
    },
  ];
}

export function buildSecondaryActions(
  item: ReviewTileItem,
  hooks: TileHooks,
  handleAction: (action: string) => Promise<void>,
  isAnyOperationInProgress: boolean
) {
  const { copyHook, commentHook } = hooks;

  return [
    {
      name: "copy",
      icon: <FaRegCopy className="size-3.5 text-neutral-400" />,
      tooltip: "Copy text",
      onClick: () => handleAction("copy"),
      isLoading: copyHook.copyingTileId === item.id,
      disabled: isAnyOperationInProgress,
      condition: true,
    },
    {
      name: "rate",
      icon: <ImStarEmpty className="size-3.5 text-neutral-400" />,
      tooltip: "Rate this response",
      onClick: () => handleAction("rate"),
      isLoading: false,
      disabled: isAnyOperationInProgress,
      className: "text-yellow-500",
      condition: true,
    },
    {
      name: "comment",
      icon: <IoChatbubblesOutline className="size-3.5 text-neutral-400" />,
      tooltip: "Add Comment",
      onClick: () => handleAction("comment"),
      isLoading: commentHook.addingCommentTileId === item.id,
      disabled: isAnyOperationInProgress,
      condition: true,
    },
  ];
}

export async function dispatchTileAction(
  action: string,
  item: ReviewTileItem,
  documentId: string,
  hooks: TileHooks,
  writeIt: (params: WriteItRequest) => void,
  setShowRatingDialog: (v: boolean) => void
) {
  const { insertHook, commentHook, copyHook, dismissHook, replaceHook } = hooks;

  try {
    switch (action) {
      case "write":
        writeIt({
          document_id: [documentId],
          tile_id: item.originalData?.tile_id ?? 0,
          query_term: item.query ?? "",
          document_type: "Incoming",
        });
        await insertHook.insertSingleTile(item);
        break;
      case "comment":
        await commentHook.addSingleComment(item);
        break;
      case "copy":
        await copyHook.copySingleTile(item);
        break;
      case "close":
        await dismissHook.dismissSingleTile(item);
        break;
      case "rate":
        setShowRatingDialog(true);
        break;
      case "insert":
        await replaceHook.replaceSingleTile(item);
        break;
      default:
        console.warn("Unknown tile action:", action);
    }
  } catch (error) {
    // Individual hooks handle their own errors via onError → toast.error.
    // This outer catch guards against any unexpected re-throw.
    const message = error instanceof Error ? error.message : "An unexpected error occurred.";
    toast.error(`Action failed: ${message}`);
  }
}
